import { useEffect, useState } from 'react';
import { useSkyxNewGameReadyState } from '@core/utils/skyxNewBootstrap';
import { useActiveGameStore } from '@core/gameengine/state/activeGameStore';
import { getGameFromUrl } from '@core/utils/urlParser';
import { isSkyxNewRoute } from '@core/utils/gameRouteSlugs';

const DEFAULT_FALLBACK_DURATION = 5000;

export function useSkyxNewWaitingOverlayState(): {
  visible: boolean;
  isBootstrapping: boolean;
  progress: number;
} {
  const isSkyxRoute = isSkyxNewRoute(getGameFromUrl());
  const isGameVisualReady = useSkyxNewGameReadyState();
  const phase = useActiveGameStore(state => state.phase);
  const phaseTotalDuration = useActiveGameStore(
    state => state.phaseTotalDuration
  );
  const phaseRemainingDuration = useActiveGameStore(
    state => state.phaseRemainingDuration
  );
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!isSkyxRoute || phase !== 'WAITING') {
      setProgress(0);
      return;
    }

    const startTime = Date.now();
    let animationFrameId: number;

    const totalDuration =
      phaseTotalDuration > 0 ? phaseTotalDuration : DEFAULT_FALLBACK_DURATION;
    const remainingDuration =
      phaseRemainingDuration > 0 ? phaseRemainingDuration : totalDuration;
    const initialProgress = Math.min(
      100,
      (remainingDuration / totalDuration) * 100
    );

    const updateProgress = () => {
      const elapsedTime = Date.now() - startTime;
      const elapsedRatio = elapsedTime / remainingDuration;
      const newProgress = Math.max(
        0,
        initialProgress - elapsedRatio * initialProgress
      );

      setProgress(newProgress);

      if (elapsedTime < remainingDuration) {
        animationFrameId = requestAnimationFrame(updateProgress);
      } else {
        setProgress(0);
      }
    };

    setProgress(initialProgress);
    animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isSkyxRoute, phaseTotalDuration, phaseRemainingDuration, phase]);

  if (!isSkyxRoute) {
    return { visible: false, isBootstrapping: false, progress: 0 };
  }

  const isBootstrapping = !isGameVisualReady;
  const visible = isBootstrapping || (phase === 'WAITING' && progress > 0);

  return { visible, isBootstrapping, progress };
}

/** True while the sky-x new bootstrap / waiting preloader overlay is on screen. */
export function useSkyxNewWaitingOverlayVisible(): boolean {
  return useSkyxNewWaitingOverlayState().visible;
}
